/**
 * 인증 상태 관리 스토어 (Zustand)
 * - 로그인 상태, 사용자 정보 관리
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as authApi from '../api/auth.api';
import i18n from '../i18n';
import { clearAllUserData, clearAllUserDataWithEditor } from '../utils/logoutCleanup';

interface AuthState {
  isAuthenticated: boolean;
  user: authApi.User | null;
  accessToken: string | null;
  _hasHydrated: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setUser: (user: authApi.User) => void;
  setToken: (accessToken: string, refreshToken?: string) => void;
  clearToken: () => void;
  setHasHydrated: (v: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      _hasHydrated: false,

      /**
       * 로그인
       */
      login: async (email: string, password: string) => {
        try {
          const response = await authApi.login({ email, password });

          // 이전 사용자 데이터 정리 후 새 토큰 설정
          clearAllUserData();
          localStorage.setItem('access_token', response.access_token);

          set({
            isAuthenticated: true,
            user: response.user,
            accessToken: response.access_token,
          });

          if (response.user.preferences?.language) {
            i18n.changeLanguage(response.user.preferences!.language!);
          }

        } catch (error) {
          console.error('❌ 로그인 실패:', error);
          throw error;
        }
      },

      /**
       * 회원가입
       */
      register: async (email: string, password: string, name: string) => {
        try {
          const response = await authApi.register({ email, password, name });

          // 이전 사용자 데이터 정리 후 새 토큰 설정
          clearAllUserData();
          localStorage.setItem('access_token', response.access_token);

          set({
            isAuthenticated: true,
            user: response.user,
            accessToken: response.access_token,
          });

          if (response.user.preferences?.language) {
            i18n.changeLanguage(response.user.preferences!.language!);
          }

        } catch (error) {
          console.error('❌ 회원가입 실패:', error);
          throw error;
        }
      },

      /**
       * 로그아웃
       */
      logout: () => {
        // 사용자별 데이터 전체 정리 (토큰, 캐시, 스토어, IndexedDB + 에디터 리셋)
        clearAllUserDataWithEditor();

        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
        });
      },

      /**
       * 사용자 정보 새로고침
       */
      refreshUser: async () => {
        try {
          const user = await authApi.getProfile();
          set({ user });
        } catch (error) {
          console.error('❌ 사용자 정보 새로고침 실패:', error);
          // 인증 실패 시 로그아웃
          get().logout();
        }
      },

      /**
       * 사용자 정보 설정
       */
      setUser: (user: authApi.User) => {
        set({ user });
      },

      /**
       * OAuth 콜백 등 외부 토큰 설정
       */
      setToken: (accessToken: string, refreshToken?: string) => {
        localStorage.setItem('access_token', accessToken);
        if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
        set({ accessToken, isAuthenticated: true });
      },

      /**
       * 토큰 및 인증 상태 초기화
       */
      clearToken: () => {
        clearAllUserDataWithEditor();
        set({ accessToken: null, isAuthenticated: false, user: null });
      },

      setHasHydrated: (v: boolean) => {
        set({ _hasHydrated: v });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        accessToken: state.accessToken,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
        // 재수화 시점에 user.role이 누락(legacy)이거나 토큰만 있고 user 정보가 없는 경우
        // 백엔드 /users/me 로 갱신 — admin 세션이 stale role로 부활하는 회귀 방어
        if (state?.isAuthenticated && (!state.user || !state.user.role)) {
          void state.refreshUser();
        }
      },
    }
  )
);

